import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { login, register } from '../services/auth';
import { DealerLogo } from '../components/DealerLogo';
import './limaLogin.css';

const Login: React.FC = () => {
  // The browser tab says this is the sign-in screen, and goes back on leaving it.
  useEffect(() => {
    const before = document.title;
    document.title = 'LIMA | Portal & Shipment | Login';
    return () => { document.title = before; };
  }, []);
  const [tab, setTab] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const dealershipId = searchParams.get('dealershipId');
  const initialTab = searchParams.get('tab');
  const [dealerBranding, setDealerBranding] = useState<{ dealershipName: string; hasLogo: boolean } | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (initialTab === 'register') setTab(1);
    if (initialTab === 'forgot') setTab(2);
  }, [initialTab]);

  useEffect(() => {
    if (!dealershipId) return;
    fetch(`/.netlify/functions/dealer-branding?dealershipId=${encodeURIComponent(dealershipId)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setDealerBranding({ dealershipName: data.dealershipName, hasLogo: data.hasLogo }))
      .catch(() => {});
  }, [dealershipId]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await login(email, password);
      navigate('/');
      window.location.reload();
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Email and password are required');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await register(email, password, fullName || undefined);
      navigate('/');
      window.location.reload();
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Forgot password: emails a link to set a new one. The answer is the same for any email.
  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError('Please enter your email address'); return; }
    setError(null); setNotice(null); setLoading(true);
    try {
      const res = await fetch('/.netlify/functions/forgot-password', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: email.trim() }) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || 'Something went wrong. Please try again.');
      setNotice('Check your email for a reset link.');
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setError(null);
    setNotice(null);
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setFullName('');
  };

  const switchTo = (t: number) => { setTab(t); resetForm(); };
  // Going to "forgot password" keeps the email already typed.
  const toForgot = () => { setTab(2); setError(null); setNotice(null); setPassword(''); };
  const brand = dealershipId && dealerBranding ? dealerBranding.dealershipName : null;
  const account = brand ? `${brand} account` : 'LIMA account';

  return (
    <div className="lima-login">
      <div className="ll-card">
        {dealershipId ? (
          <DealerLogo
            dealershipId={dealershipId}
            dealershipName={dealerBranding?.dealershipName || 'Dealer Portal'}
            variant="login"
            showFallback={true}
            sx={{ height: 44, maxHeight: 44, width: 'auto', mx: 'auto', display: 'block' }}
          />
        ) : (
          <a href="https://limaautomotive.com/" aria-label="LIMA Automotive home" style={{ display: 'block', width: 'fit-content', margin: '0 auto' }}><img className="ll-logo" src="/LIMA_LOGO_LOGIN.png" alt="LIMA Automotive" /></a>
        )}
        <span className="ll-pill">Shipment Portal</span>
        <h1 className="ll-title">{tab === 0 ? 'Sign in' : tab === 1 ? 'Create account' : 'Reset password'}</h1>
        <p className="ll-sub">{tab === 0 ? `Sign in to your ${account}` : tab === 1 ? `Create your ${account}` : 'We will email you a link to set a new password'}</p>
        <div className="ll-msg" role="alert" aria-live="polite">
          {error ? <div className="ll-alert err" title={error}>{error}</div> : notice ? <div className="ll-alert ok" title={notice}>{notice}</div> : null}
        </div>

        {tab === 0 ? (
          <form className="ll-form" onSubmit={handleLogin} noValidate>
            <label className="ll-field" htmlFor="login-email">
              <span className="ll-label">Email address</span>
              <input
                className="ll-input"
                required
                id="login-email"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </label>
            <label className="ll-field" htmlFor="login-password">
              <span className="ll-label">Password</span>
              <input
                className="ll-input"
                required
                name="password"
                type="password"
                id="login-password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
            </label>
            <button type="submit" className="ll-btn" disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
            <button type="button" className="ll-link ll-forgot" onClick={toForgot}>Forgot password?</button>
          </form>
        ) : tab === 2 ? (
          <form className="ll-form" onSubmit={handleForgot} noValidate>
            <label className="ll-field" htmlFor="forgot-email">
              <span className="ll-label">Email address</span>
              <input
                className="ll-input"
                required
                id="forgot-email"
                name="email"
                type="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </label>
            <button type="submit" className="ll-btn" disabled={loading}>
              {loading ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
        ) : (
          <form className="ll-form" onSubmit={handleRegister} noValidate>
            <label className="ll-field" htmlFor="register-name">
              <span className="ll-label">Full name</span>
              <input
                className="ll-input"
                id="register-name"
                name="fullName"
                autoComplete="name"
                autoFocus
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                disabled={loading}
              />
            </label>
            <label className="ll-field" htmlFor="register-email">
              <span className="ll-label">Email address</span>
              <input
                className="ll-input"
                required
                id="register-email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
              />
            </label>
            <label className="ll-field" htmlFor="register-password">
              <span className="ll-label">Password</span>
              <input
                className="ll-input"
                required
                name="password"
                type="password"
                id="register-password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              <span className="ll-help">At least 6 characters</span>
            </label>
            <label className="ll-field" htmlFor="register-confirm-password">
              <span className="ll-label">Confirm password</span>
              <input
                className="ll-input"
                required
                name="confirmPassword"
                type="password"
                id="register-confirm-password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
            </label>
            <button type="submit" className="ll-btn" disabled={loading}>
              {loading ? 'Creating account…' : 'Create account'}
            </button>
          </form>
        )}
        <p className="ll-alt">
          {tab === 2 ? (
            <>Remembered it? <button type="button" className="ll-link" onClick={() => switchTo(0)}>Back to sign in</button></>
          ) : tab === 0 ? (
            <>Don't have an account? <button type="button" className="ll-link" onClick={() => switchTo(1)}>Create one</button></>
          ) : (
            <>Already have an account? <button type="button" className="ll-link" onClick={() => switchTo(0)}>Sign in</button></>
          )}
        </p>
      </div>
      <div className="ll-foot">LIMA Automotive Industries<br /><span style={{ whiteSpace: 'nowrap' }}>Toll Free +1 877 298-0911</span></div>
    </div>
  );
};

export default Login;
